import { 
  collection, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  getDoc,
  updateDoc,
  Timestamp,
  limit,
  writeBatch,
} from 'firebase/firestore';
import { db } from './config';

/**
 * Notification types
 */
export const NOTIFICATION_TYPES = {
  WISHLIST_PRICE_DROP: 'wishlist_price_drop',
  WISHLIST_AVAILABLE: 'wishlist_available',
  MARKETPLACE_OFFER: 'marketplace_offer',
  MARKETPLACE_OFFER_ACCEPTED: 'marketplace_offer_accepted',
  MARKETPLACE_OFFER_REJECTED: 'marketplace_offer_rejected',
  MARKETPLACE_OFFER_COUNTERED: 'marketplace_offer_countered',
  MARKETPLACE_SALE: 'marketplace_sale',
  MARKETPLACE_WATCHLIST: 'marketplace_watchlist',
  FAVORITES_SHOWCASE: 'favorites_showcase',
  COLLECTION_MILESTONE: 'collection_milestone',
  NEW_SET_RELEASE: 'new_set_release',
  SOCIAL_FOLLOW: 'social_follow',
  SOCIAL_LIKE: 'social_like',
  CHAT_MESSAGE: 'chat_message',
  SYSTEM_ANNOUNCEMENT: 'system_announcement'
};

/**
 * Create a notification via trusted Cloud Function.
 * Admin/founder only — social follow/reaction/message use server triggers.
 */
export const createNotification = async (notificationData) => {
  try {
    const { getCallable } = await import('./config');
    const createFn = await getCallable('createNotification');
    const result = await createFn({
      userId: notificationData.userId,
      type: notificationData.type,
      title: notificationData.title,
      message: notificationData.message,
      data: notificationData.data || {},
      expiresAt: notificationData.expiresAt || null,
      actionUrl: notificationData.actionUrl || null,
      imageUrl: notificationData.imageUrl || null,
      priority: notificationData.priority || 'normal',
      category: notificationData.category || 'general',
    });
    return {
      id: result.data?.id,
      userId: notificationData.userId,
      type: notificationData.type,
      title: notificationData.title,
      message: notificationData.message,
      data: notificationData.data || {},
      read: false,
      ...notificationData,
    };
  } catch (error) {
    console.error('Error creating notification:', error);
    throw new Error('Failed to create notification: ' + error.message);
  }
};

/**
 * Get user notifications
 */
export const getUserNotifications = async (userId, filters = {}) => {
  try {
    let q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId)
    );

    // Apply filters
    if (filters.unreadOnly) {
      q = query(q, where('read', '==', false));
    }
    
    if (filters.category) {
      q = query(q, where('category', '==', filters.category));
    }
    
    if (filters.type) {
      q = query(q, where('type', '==', filters.type));
    }

    // Add sorting and limits
    q = query(q, orderBy('dateCreated', 'desc'));
    
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw new Error('Failed to fetch notifications: ' + error.message);
  }
};

/**
 * Mark notification(s) as read.
 * Wave 5: only decrement for notifications that were previously unread
 * (avoids double-decrement). Uses unreadNotificationCount (server field).
 */
export const markAsRead = async (userId, notificationIds) => {
  try {
    const ids = Array.isArray(notificationIds) ? notificationIds : [notificationIds];
    if (!userId || ids.length === 0) return true;

    const snaps = await Promise.all(
      ids.map((id) => getDoc(doc(db, 'notifications', id))),
    );
    const unreadIds = snaps
      .filter((s) => s.exists() && s.data().read !== true && s.data().userId === userId)
      .map((s) => s.id);

    if (unreadIds.length === 0) return true;

    const userSnap = await getDoc(doc(db, 'users', userId));
    const current = Number(userSnap.data()?.unreadNotificationCount) || 0;
    const next = Math.max(0, current - unreadIds.length);

    const batch = writeBatch(db);
    const now = Timestamp.now();
    unreadIds.forEach((id) => {
      batch.update(doc(db, 'notifications', id), {
        read: true,
        dateRead: now,
      });
    });
    batch.update(doc(db, 'users', userId), {
      unreadNotificationCount: next,
      unreadNotifications: next,
    });
    await batch.commit();

    return true;
  } catch (error) {
    console.error('Error marking notifications as read:', error);
    throw new Error('Failed to mark notifications as read: ' + error.message);
  }
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (userId) => {
  try {
    const unreadQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('read', '==', false)
    );
    
    const unreadSnapshot = await getDocs(unreadQuery);
    
    if (unreadSnapshot.empty) {
      return true;
    }

    const updatePromises = unreadSnapshot.docs.map(doc => 
      updateDoc(doc.ref, {
        read: true,
        dateRead: Timestamp.now()
      })
    );

    await Promise.all(updatePromises);
    await resetUnreadCount(userId);
    
    return true;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw new Error('Failed to mark all notifications as read: ' + error.message);
  }
};

/**
 * Delete notification
 */
export const deleteNotification = async (notificationId) => {
  try {
    await deleteDoc(doc(db, 'notifications', notificationId));
    return true;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw new Error('Failed to delete notification: ' + error.message);
  }
};

/**
 * Unread badge count from the denormalized user field (no full notifications scan).
 */
export const getUnreadNotificationCount = async (userId) => {
  try {
    const userSnap = await getDoc(doc(db, 'users', userId));
    if (!userSnap.exists()) return 0;
    const d = userSnap.data() || {};
    // Canonical: unreadNotificationCount (server). Legacy alias fallback.
    const n = d.unreadNotificationCount ?? d.unreadNotifications;
    return typeof n === 'number' && n > 0 ? n : 0;
  } catch (error) {
    console.error('Error fetching unread notification count:', error);
    return 0;
  }
};

/**
 * Lightweight stats for UI — uses stored unread count + a capped recent window.
 * Does NOT download the user's entire notifications collection.
 */
export const getNotificationStats = async (userId) => {
  try {
    const [unread, recent] = await Promise.all([
      getUnreadNotificationCount(userId),
      getUserNotifications(userId, { limit: 20 }).catch(() => []),
    ]);

    const categoryBreakdown = {};
    const typeBreakdown = {};
    (recent || []).forEach((notification) => {
      const category = notification.category || 'general';
      categoryBreakdown[category] = (categoryBreakdown[category] || 0) + 1;
      const type = notification.type;
      if (type) typeBreakdown[type] = (typeBreakdown[type] || 0) + 1;
    });

    return {
      total: null, // unknown without full scan — callers should not rely on this
      unread,
      read: null,
      categoryBreakdown,
      typeBreakdown,
      recentActivity: (recent || []).slice(0, 5),
    };
  } catch (error) {
    console.error('Error fetching notification stats:', error);
    throw new Error('Failed to fetch notification statistics: ' + error.message);
  }
};

/**
 * Create wishlist price alert notification
 */
export const createWishlistPriceAlert = async (userId, cardId, cardName, oldPrice, newPrice, listingId) => {
  try {
    return await createNotification({
      userId,
      type: NOTIFICATION_TYPES.WISHLIST_PRICE_DROP,
      title: 'Price Drop Alert! 📉',
      message: `${cardName} dropped from $${oldPrice} to $${newPrice}`,
      data: {
        cardId,
        cardName,
        oldPrice,
        newPrice,
        listingId,
        savingsAmount: oldPrice - newPrice,
        savingsPercent: Math.round(((oldPrice - newPrice) / oldPrice) * 100)
      },
      actionUrl: `/marketplace/listings/${listingId}`,
      priority: 'high',
      category: 'wishlist'
    });
  } catch (error) {
    console.error('Error creating wishlist price alert:', error);
    throw error;
  }
};

/**
 * Create marketplace offer notification
 */
export const createMarketplaceOfferNotification = async (sellerId, buyerName, amount, cardName, offerId) => {
  try {
    return await createNotification({
      userId: sellerId,
      type: NOTIFICATION_TYPES.MARKETPLACE_OFFER,
      title: 'New Offer Received! 💰',
      message: `${buyerName} offered $${amount} for your ${cardName}`,
      data: {
        offerId,
        buyerName,
        amount,
        cardName
      },
      actionUrl: `/marketplace/offers/${offerId}`,
      priority: 'high',
      category: 'marketplace'
    });
  } catch (error) {
    console.error('Error creating marketplace offer notification:', error);
    throw error;
  }
};

/**
 * Create collection milestone notification
 */
export const createCollectionMilestoneNotification = async (userId, milestone, description) => {
  try {
    return await createNotification({
      userId,
      type: NOTIFICATION_TYPES.COLLECTION_MILESTONE,
      title: `Collection Milestone! 🎉`,
      message: description,
      data: {
        milestone,
        achievedAt: Timestamp.now()
      },
      priority: 'normal',
      category: 'general'
    });
  } catch (error) {
    console.error('Error creating collection milestone notification:', error);
    throw error;
  }
};

/**
 * Create new set release notification
 */
export const createNewSetNotification = async (userId, setName, setId, releaseDate) => {
  try {
    return await createNotification({
      userId,
      type: NOTIFICATION_TYPES.NEW_SET_RELEASE,
      title: 'New Set Released! 🆕',
      message: `${setName} is now available for collection`,
      data: {
        setName,
        setId,
        releaseDate
      },
      actionUrl: `/sets/${setId}`,
      priority: 'normal',
      category: 'general'
    });
  } catch (error) {
    console.error('Error creating new set notification:', error);
    throw error;
  }
};

/**
 * Facebook-style reaction notification for profile showcase content.
 * actionUrl → content; data.reactorProfileUrl → reactor's profile.
 */
/** @deprecated Server trigger owns reaction notifications. */
export const createSocialReactionNotification = async () => ({
  success: true,
  serverAuthored: true,
});

/**
 * Helper — Wave 5: unreadNotificationCount is CF-owned on create;
 * clients may only reset/decrement via mark-read paths (protected field
 * otherwise). reset still allowed for mark-all-as-read.
 */
const resetUnreadCount = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      unreadNotificationCount: 0,
      unreadNotifications: 0, // clear legacy alias if present
    });
  } catch (error) {
    console.warn('Failed to reset unread count:', error);
  }
};

/**
 * Batch create notifications for multiple users
 */
export const createBulkNotifications = async (notifications) => {
  try {
    const createPromises = notifications.map(notification => createNotification(notification));
    const results = await Promise.all(createPromises);
    return results;
  } catch (error) {
    console.error('Error creating bulk notifications:', error);
    throw new Error('Failed to create bulk notifications: ' + error.message);
  }
};

/**
 * Subscribe to notification preferences
 */
export const updateNotificationPreferences = async (userId, preferences) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      notificationPreferences: {
        email: preferences.email || false,
        push: preferences.push || false,
        wishlistAlerts: preferences.wishlistAlerts !== false, // default true
        marketplaceOffers: preferences.marketplaceOffers !== false, // default true
        socialUpdates: preferences.socialUpdates !== false, // default true
        systemAnnouncements: preferences.systemAnnouncements !== false, // default true
        ...preferences
      }
    });
    return true;
  } catch (error) {
    console.error('Error updating notification preferences:', error);
    throw new Error('Failed to update notification preferences: ' + error.message);
  }
};

/**
 * Get user notification preferences
 */
export const getNotificationPreferences = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return userSnap.data().notificationPreferences || {
        email: false,
        push: false,
        wishlistAlerts: true,
        marketplaceOffers: true,
        socialUpdates: true,
        systemAnnouncements: true
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching notification preferences:', error);
    throw new Error('Failed to fetch notification preferences: ' + error.message);
  }
}; 